/*
 * RiderApp — the consumer PWA foundation.
 *
 * URL:   /r/:token
 * Flow:  destination (voice/text) → vehicle type → 4 drivers → hail → live map
 *
 * States:
 *   'compose'   — enter destination + pick vehicle type
 *   'picking'   — 4 driver cards to choose from
 *   'waiting'   — hail sent to driver, awaiting accept (20s TTL)
 *   'accepted'  — driver confirmed, live map
 *   'exhausted' — no driver accepted, retry
 *
 * Voice: uses Web Speech API (browser native, no server call for transcription).
 * Uses your existing REACT_APP_BACKEND_URL through the api client.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline } from "react-leaflet";
import TiltMap3D from "../components/TiltMap3D";
import MedexOvaColdLoad from "../components/MedexOvaColdLoad";
import CeremonyHalo from "../components/motion/CeremonyHalo";
import { TierBadge, StreakFlame, DailyGoalCard } from "../components/gamify/GamifyPrimitives";
import { AnonymizedTicker } from "../components/live/LiveWorld";
import L from "leaflet";
import polyline from "@mapbox/polyline";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import InstallPromptModal, { isStandalone } from "../components/InstallPromptModal";
import usePushNotifications from "../hooks/usePushNotifications";
import { AnimatedNumber, WaitRing, fireConfetti, haptic } from "../components/motion/MotionPrimitives";

let _stripePromise = null;
async function getStripe() {
  if (_stripePromise) return _stripePromise;
  const { data } = await apiClient.get("/rider/payments/config");
  _stripePromise = loadStripe(data.publishable_key);
  return _stripePromise;
}

/* Google Places-backed autocomplete for the RIDER destination field.
   Nominatim doesn't find POIs like "DaVita Dialysis" — Google does. Uses our
   backend proxy so the API key stays server-side. */
function GooglePlacesInput({ value, onChange, onPlace, testid, placeholder, biasLat, biasLng }) {
  const [preds, setPreds] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const debounce = React.useRef(null);
  const wrap = React.useRef(null);
  React.useEffect(() => {
    const onDoc = (e) => wrap.current && !wrap.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const handleChange = (e) => {
    const q = e.target.value;
    onChange(q);
    clearTimeout(debounce.current);
    if (q.length < 2) { setPreds([]); setOpen(false); return; }
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { q };
        if (biasLat != null && biasLng != null) { params.lat = biasLat; params.lng = biasLng; }
        const { data } = await apiClient.get("/rider/places/autocomplete", { params });
        setPreds(data.predictions || []);
        setOpen(true);
      } catch { setPreds([]); }
      finally { setLoading(false); }
    }, 300);
  };
  const pick = async (p) => {
    setOpen(false);
    onChange(p.description);
    try {
      const { data } = await apiClient.get(`/rider/places/details/${encodeURIComponent(p.place_id)}`);
      if (data.lat != null && data.lng != null) onPlace({ formatted_address: data.formatted_address || p.description, lat: data.lat, lng: data.lng });
    } catch (err) { toast.error("Could not resolve that place — try another"); }
  };
  return (
    <div className="relative" ref={wrap}>
      <input
        type="text"
        value={value || ""}
        onChange={handleChange}
        onFocus={() => preds.length && setOpen(true)}
        placeholder={placeholder}
        data-testid={testid}
        autoComplete="off"
        className="w-full pl-9 pr-11 border border-[#E2E0D8] rounded-md py-2 text-base"
        style={{ paddingLeft: 34 }}
      />
      <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}>📍</span>
      {loading && <span style={{ position: "absolute", right: 44, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#94a3b8" }}>…</span>}
      {open && preds.length > 0 && (
        <div className="absolute z-[10000] left-0 right-0 mt-1 bg-white border border-[#E2E0D8] rounded-md shadow-lg max-h-64 overflow-y-auto"
             data-testid={`${testid}-suggestions`}>
          {preds.map((p, i) => (
            <button
              type="button"
              key={p.place_id}
              onMouseDown={(e) => { e.preventDefault(); pick(p); }}
              className="block w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
              data-testid={`${testid}-suggestion-${i}`}
            >
              <div className="text-slate-900 truncate font-semibold">{p.main_text || p.description}</div>
              {p.secondary_text && <div className="text-[11px] text-slate-500 truncate">{p.secondary_text}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const VEHICLE_TYPES = [
  { id: "sedan",      label: "Sedan",         emoji: "🚗", sub: "Solo or small group" },
  { id: "wheelchair", label: "Wheelchair",    emoji: "♿", sub: "Ramp / lift access" },
  { id: "xl_van",     label: "XL / Van",      emoji: "🚐", sub: "Up to 6 riders" },
];

const riderIcon = L.divIcon({
  className: "astat-rider-pin",
  html: '<div style="width:22px;height:22px;border-radius:50%;background:#3A7BD5;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4)"></div>',
  iconSize: [22, 22], iconAnchor: [11, 11],
});
const driverIcon = L.divIcon({
  className: "astat-driver-pin",
  html: '<div style="width:32px;height:32px;border-radius:50%;background:#2B4C3B;color:#fff;display:grid;place-items:center;font-size:15px;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.4)">🚗</div>',
  iconSize: [32, 32], iconAnchor: [16, 16],
});

// Smooth driver marker — animates from previous polled position to newest one
// over ~2.5s (matches our 3s poll interval). Prevents the marker from "teleporting"
// every 3s. Bible §Motion Cue: continuous cue while state is active.
function SmoothDriverMarker({ position, name }) {
  const markerRef = React.useRef(null);
  const fromRef = React.useRef(null);   // {lat,lng,at}
  const toRef = React.useRef(null);
  const rafRef = React.useRef(null);
  const startedRef = React.useRef(0);

  React.useEffect(() => {
    if (!position?.lat || !position?.lng) return;
    // First update — snap; subsequent — lerp
    if (!toRef.current) {
      toRef.current = { lat: position.lat, lng: position.lng };
      if (markerRef.current) markerRef.current.setLatLng([position.lat, position.lng]);
      return;
    }
    // Same coords — skip
    if (toRef.current.lat === position.lat && toRef.current.lng === position.lng) return;
    fromRef.current = { ...toRef.current };
    toRef.current = { lat: position.lat, lng: position.lng };
    startedRef.current = performance.now();

    const DURATION = 2500;
    const step = (now) => {
      const t = Math.min(1, (now - startedRef.current) / DURATION);
      // ease-out cubic — feels natural for vehicle motion
      const e = 1 - Math.pow(1 - t, 3);
      const lat = fromRef.current.lat + (toRef.current.lat - fromRef.current.lat) * e;
      const lng = fromRef.current.lng + (toRef.current.lng - fromRef.current.lng) * e;
      if (markerRef.current) markerRef.current.setLatLng([lat, lng]);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [position?.lat, position?.lng]);

  if (!position?.lat) return null;
  return (
    <Marker
      position={[position.lat, position.lng]}
      icon={driverIcon}
      ref={(el) => { if (el) markerRef.current = el; }}
    >
      <Popup>{name}</Popup>
    </Marker>
  );
}

export default function RiderApp() {
  const { token } = useParams();
  const [rider, setRider] = useState(null);
  // Install gate — read the "skipped" flag from localStorage so we don't re-prompt
  // every load once the user has explicitly opted out. Reset per token.
  const [installGateOpen, setInstallGateOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    if (isStandalone()) return false;
    const skipKey = "astat.install.skip.rider";
    try {
      const v = window.localStorage.getItem(skipKey);
      if (v === "1") return false;
    } catch { /* ignore */ }
    return true;
  });
  const push = usePushNotifications({ riderToken: token });
  const [step, setStep] = useState("compose");
  const [pickup, setPickup] = useState(null);           // {address, lat, lng}
  const [dropoff, setDropoff] = useState({ address: "" });
  const [vehicleType, setVehicleType] = useState("sedan");
  const [quote, setQuote] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [tripId, setTripId] = useState(null);
  const [tripStatus, setTripStatus] = useState(null);   // full trip doc
  const [driverInfo, setDriverInfo] = useState(null);
  const [voiceListening, setVoiceListening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPmId, setDefaultPmId] = useState(null);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [addCardClientSecret, setAddCardClientSecret] = useState(null);
  const recognitionRef = useRef(null);

  const hasCard = paymentMethods.length > 0 && !!defaultPmId;

  // Load Stripe.js publishable key once
  useEffect(() => { getStripe().then(setStripePromise); }, []);

  // Load payment methods when rider is known
  const refreshPaymentMethods = async () => {
    try {
      const { data } = await apiClient.get(`/rider/payments/methods/${token}`);
      setPaymentMethods(data.methods || []);
      setDefaultPmId(data.default_payment_method_id || (data.methods?.[0]?.id ?? null));
    } catch { /* keep silent */ }
  };
  useEffect(() => {
    if (!rider) return;
    refreshPaymentMethods();
  }, [rider]);

  const openAddCard = async () => {
    try {
      const { data } = await apiClient.post(`/rider/payments/setup-intent/${token}`);
      setAddCardClientSecret(data.client_secret);
      setAddCardOpen(true);
    } catch (err) {
      toast.error("Could not start card setup — please try again");
    }
  };

  // Once installed (or user skipped), auto-prompt for notification permission if we haven't yet.
  useEffect(() => {
    if (installGateOpen) return;
    if (!push.supported) return;
    if (push.permission !== "default") return;
    // Small delay so it doesn't fire before the page renders
    const t = setTimeout(() => { push.enable().catch(() => {}); }, 900);
    return () => clearTimeout(t);
  }, [installGateOpen, push.supported, push.permission]);

  const onCardAdded = async (paymentMethodId) => {
    setAddCardOpen(false);
    setAddCardClientSecret(null);
    try {
      await apiClient.post("/rider/payments/methods/set-default", {
        token, payment_method_id: paymentMethodId,
      });
    } catch { /* non-fatal */ }
    await refreshPaymentMethods();
    toast.success("Card saved");
  };

  // 1. Load rider identity from the URL token
  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get(`/rider/me/${token}`);
        setRider(data);
      } catch (err) {
        toast.error("This ride link isn't valid. Please request a new one.");
      }
    })();
  }, [token]);

  // 2. Ask for geolocation ONCE on mount → pickup auto-fill
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setPickup({
          address: "Current location",
          lat: p.coords.latitude,
          lng: p.coords.longitude,
        });
      },
      () => { /* rider can still type an address */ },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []);

  // 3. Voice input for the destination field (Web Speech API)
  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.info("Voice not supported on this browser — please type your destination.");
      return;
    }
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const text = e.results[0][0].transcript;
      setDropoff((prev) => ({ ...prev, address: text }));
      setVoiceListening(false);
    };
    rec.onerror = (e) => {
      setVoiceListening(false);
      toast.error(`Voice: ${e.error}`);
    };
    rec.onend = () => setVoiceListening(false);
    recognitionRef.current = rec;
    setVoiceListening(true);
    rec.start();
  };

  // 4. Fetch quote + rank drivers
  const findRides = async (opts = {}) => {
    const { expand = false } = opts;
    if (!pickup?.lat) { toast.error("We need your pickup location. Please allow location access or type an address."); return; }
    if (!dropoff.address?.trim()) { toast.error("Where would you like to go?"); return; }
    setBusy(true);
    try {
      const qRes = quote || (await apiClient.post("/rider/quote", {
        pickup_address: pickup.address === "Current location"
          ? `${pickup.lat},${pickup.lng}`  // let backend reverse-geocode
          : pickup.address,
        dropoff_address: dropoff.address,
        vehicle_type: vehicleType,
      })).data ? null : null;
      // Actually always fetch a fresh quote on first pass (skip on retry)
      let q = quote;
      if (!q) {
        const r = await apiClient.post("/rider/quote", {
          pickup_address: pickup.address === "Current location"
            ? `${pickup.lat},${pickup.lng}`
            : pickup.address,
          dropoff_address: dropoff.address,
          vehicle_type: vehicleType,
        });
        q = r.data;
        setQuote(q);
        setPickup((p) => ({ ...p, ...q.pickup }));
        setDropoff({ ...q.dropoff });
      }

      const dRes = await apiClient.get("/rider/nearest-drivers", {
        params: {
          lat: q.pickup.lat,
          lng: q.pickup.lng,
          vehicle_type: vehicleType,
          debug: true,
          expand: expand ? "true" : "false",
        },
      });
      const list = dRes.data.drivers || [];
      if (!list.length) {
        const dbg = dRes.data.debug;
        const scanned = dbg?.total_scanned ?? 0;
        if (scanned === 0) {
          toast.error(`No ${vehicleType.replace("_"," ")} drivers are on the network yet in your area.`);
        } else if (!expand) {
          // Auto-retry with expanded radius
          toast(`Searching a wider area…`, { duration: 3000 });
          setBusy(false);
          return findRides({ expand: true });
        } else {
          toast.error(
            `No ${vehicleType.replace("_", " ")} drivers are free close enough right now. Please try again shortly.`,
            { duration: 8000 }
          );
        }
        return;
      }
      if (expand) {
        toast.success(`Found ${list.length} driver${list.length > 1 ? "s" : ""} in the wider area.`);
      }
      setCandidates(list);
      setStep("picking");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not fetch rides");
    } finally {
      setBusy(false);
    }
  };

  // 5. Hail a specific driver
  const hail = async (driver) => {
    if (busy) return;
    if (!hasCard) {
      toast.error("Please add a payment card first");
      openAddCard();
      return;
    }
    setBusy(true);
    try {
      const rankedIds = candidates.map((c) => c.driver_id);
      const { data } = await apiClient.post(`/rider/hail?token=${encodeURIComponent(token)}`, {
        driver_id: driver.driver_id,
        pickup_address: pickup.address,
        pickup_lat: pickup.lat, pickup_lng: pickup.lng,
        dropoff_address: dropoff.address,
        dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng,
        vehicle_type: vehicleType,
        fare_quote: quote.fare,
        ranked_driver_ids: rankedIds,
      });
      setTripId(data.trip_id);
      setDriverInfo(driver);
      setStep("waiting");
      // Handle 3DS requires_action inline if Stripe demanded it
      if (data.payment_requires_action && data.payment_client_secret && stripePromise) {
        const stripe = await stripePromise;
        const { error } = await stripe.confirmCardPayment(data.payment_client_secret);
        if (error) {
          toast.error(`Payment authentication failed: ${error.message}`);
          try { await apiClient.post(`/rider/rides/${data.trip_id}/cancel`); } catch { /* */ }
          setStep("compose");
          setTripId(null);
        }
      }
    } catch (err) {
      const detail = formatApiErrorDetail(err?.response?.data?.detail) || "Hail failed";
      const status = err?.response?.status;
      if (status === 402) {
        toast.error(detail);
        openAddCard();
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  // 6. Poll trip status every 3s while waiting/accepted
  useEffect(() => {
    if (!tripId || step === "compose" || step === "picking") return;
    let stopped = false;
    const tick = async () => {
      try {
        const { data } = await apiClient.get(`/rider/rides/${tripId}`);
        if (stopped) return;
        setTripStatus(data.trip);
        if (data.driver) setDriverInfo((prev) => ({ ...(prev || {}), ...data.driver }));
        const s = data.trip?.status;
        // Map the unified NEMT-style statuses to the rider's step machine.
        // OFFERED = waiting for accept; EN_ROUTE = driver en route to me;
        // ARRIVED_PU = driver at pickup; ON_TRIP = we're driving to destination;
        // ARRIVED_DO / COMPLETED = trip done.
        const acceptedish = ["ACCEPTED", "EN_ROUTE", "ARRIVED_PU", "ON_TRIP", "ARRIVED_DO"];
        // Fire a haptic pulse on transitions the rider needs to feel
        const prev = tripStatus?.status;
        if (prev !== s) {
          if (s === "EN_ROUTE") haptic(30);
          if (s === "ARRIVED_PU") haptic([60, 40, 60, 40, 100]);
          if (s === "ON_TRIP") haptic(40);
        }
        if (acceptedish.includes(s) && step !== "accepted") setStep("accepted");
        if (s === "OFFER_EXHAUSTED") setStep("exhausted");
        if (s === "COMPLETED") setStep("completed");
        if (s === "CANCELED_BY_RIDER" || s === "CANCELED" || s === "CANCELLED") setStep("compose");
      } catch (err) { /* keep trying */ }
    };
    tick();
    const iv = setInterval(tick, 3000);
    return () => { stopped = true; clearInterval(iv); };
  }, [tripId, step]);

  const cancelHail = async () => {
    if (!tripId) return;
    try {
      await apiClient.post(`/rider/rides/${tripId}/cancel`);
      toast.info("Ride canceled.");
      setStep("compose");
      setTripId(null);
      setDriverInfo(null);
      setCandidates([]);
    } catch (err) {
      toast.error("Could not cancel");
    }
  };

  // ---------- render ----------
  if (!rider) {
    return <MedexOvaColdLoad role="rider" minMs={1400} />;
  }

  return (
    <div className="min-h-screen bg-[#F5F3EA] text-slate_navy" data-testid="rider-app">
      <CeremonyHalo trigger={tripStatus?.status} role="rider" />
      {installGateOpen && (
        <InstallPromptModal
          audience="rider"
          onInstalled={() => setInstallGateOpen(false)}
          onSkip={() => {
            try { window.localStorage.setItem("astat.install.skip.rider", "1"); } catch { /* */ }
            setInstallGateOpen(false);
          }}
        />
      )}
      <header className="px-4 pt-6 pb-3 flex items-center justify-between">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate-500 font-bold">MedexOVA Ride</div>
          <div className="text-sm text-slate-600">Hi{rider.name ? `, ${rider.name.split(" ")[0]}` : ""}. Where to?</div>
        </div>
        <div className="flex items-center gap-2">
          {step === "compose" && (
            <button
              onClick={() => setStep("history")}
              data-testid="rider-history-btn"
              className="text-[11px] px-3 py-1.5 rounded-full font-semibold"
              style={{ background: "#F5F3EA", color: "#2B4C3B", border: "1px solid #E2E0D8" }}
            >My rides</button>
          )}
          <div className="text-[10px] text-slate-400 font-mono hidden sm:block">/ride/{token}</div>
        </div>
      </header>

      {step === "history" && (
        <HistoryStep
          token={token}
          onBack={() => setStep("compose")}
          onRideAgain={(t) => {
            const pu = t.pickup || {};
            const doff = t.dropoff || {};
            if (pu.lat && pu.lng) setPickup({ address: pu.address, lat: pu.lat, lng: pu.lng });
            else if (pu.address) setPickup({ address: pu.address });
            if (doff.lat && doff.lng) setDropoff({ address: doff.address, lat: doff.lat, lng: doff.lng });
            else if (doff.address) setDropoff({ address: doff.address });
            setQuote(null);
            setStep("compose");
          }}
        />
      )}

      {step === "compose" && (
        <RiderStatusRow rider={rider} />
      )}

      {step === "compose" && (
        <ComposeStep
          token={token}
          pickup={pickup} setPickup={setPickup}
          dropoff={dropoff} setDropoff={setDropoff}
          vehicleType={vehicleType} setVehicleType={setVehicleType}
          startVoice={startVoice} voiceListening={voiceListening}
          busy={busy} findRides={findRides}
          paymentMethods={paymentMethods}
          defaultPmId={defaultPmId}
          onAddCard={openAddCard}
          onSetDefault={async (pmId) => {
            try {
              await apiClient.post("/rider/payments/methods/set-default", { token, payment_method_id: pmId });
              await refreshPaymentMethods();
            } catch { /* */ }
          }}
        />
      )}

      {step === "picking" && (
        <PickStep
          quote={quote} candidates={candidates} vehicleType={vehicleType}
          onBack={() => setStep("compose")} onHail={hail} busy={busy}
        />
      )}

      {step === "waiting" && (
        <WaitStep driver={driverInfo} trip={tripStatus} onCancel={cancelHail} />
      )}

      {step === "accepted" && (
        <AcceptedStep token={token} driver={driverInfo} trip={tripStatus} pickup={pickup} onCancel={cancelHail} />
      )}

      {step === "exhausted" && (
        <ExhaustedStep onRetry={() => { setStep("compose"); setTripId(null); }}/>
      )}

      {step === "completed" && (
        <CompletedStep
          token={token}
          trip={tripStatus}
          driver={driverInfo}
          onDone={() => {
            setStep("compose");
            setTripId(null);
            setTripStatus(null);
            setDriverInfo(null);
            setCandidates([]);
            setQuote(null);
            setDropoff({ address: "" });
          }}
        />
      )}

      {addCardOpen && addCardClientSecret && stripePromise && (
        <div className="fixed inset-0 z-[9999] bg-black/50 grid place-items-center px-4" data-testid="rider-add-card-modal">
          <div className="bg-white rounded-2xl shadow-2xl p-4 w-full max-w-md">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-bold text-slate_navy">Add a payment card</h3>
              <button onClick={() => { setAddCardOpen(false); setAddCardClientSecret(null); }} data-testid="rider-add-card-close" className="text-slate-500 text-lg">×</button>
            </div>
            <Elements stripe={stripePromise} options={{ clientSecret: addCardClientSecret, appearance: { theme: "stripe" } }}>
              <InlineCardCollector token={token} onSaved={onCardAdded} onCancel={() => { setAddCardOpen(false); setAddCardClientSecret(null); }} />
            </Elements>
          </div>
        </div>
      )}
    </div>
  );
}

function InlineCardCollector({ token, onSaved, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setErr(null);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements, redirect: "if_required",
      confirmParams: { return_url: `${window.location.origin}/ride/${token}` },
    });
    if (error) { setErr(error.message || "Card could not be saved"); setBusy(false); return; }
    setBusy(false);
    onSaved(setupIntent?.payment_method);
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="text-[11px] text-slate-500 leading-snug">
        Stored securely with Stripe. We only charge you if a driver completes your trip.
      </div>
      <PaymentElement options={{ wallets: { applePay: "auto", googlePay: "auto" } }} />
      {err && <div className="text-xs text-red-600" data-testid="rider-add-card-error">{err}</div>}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="flex-1 py-2 rounded-md border border-slate-300 text-sm">Cancel</button>
        <button type="submit" disabled={!stripe || busy} data-testid="rider-add-card-save"
          className="flex-1 py-2 rounded-md bg-forest text-white text-sm font-bold disabled:opacity-60">
          {busy ? "Saving…" : "Save card"}
        </button>
      </div>
    </form>
  );
}

/* ---------- Compose ---------- */
function ComposeStep({ token, pickup, setPickup, dropoff, setDropoff, vehicleType, setVehicleType, startVoice, voiceListening, busy, findRides, paymentMethods, defaultPmId, onAddCard, onSetDefault }) {
  const defaultPm = paymentMethods.find((p) => p.id === defaultPmId) || paymentMethods[0];
  const [saved, setSaved] = React.useState([]);
  const [savingNew, setSavingNew] = React.useState(false);
  const [newName, setNewName] = React.useState("");
  const [showVehiclePicker, setShowVehiclePicker] = React.useState(vehicleType !== "sedan");
  React.useEffect(() => {
    if (!token) return;
    apiClient.get(`/rider/destinations?token=${encodeURIComponent(token)}`)
      .then(r => setSaved(r?.data?.items || []))
      .catch(() => setSaved([]));
  }, [token]);

  async function pickSaved(d) {
    setDropoff({ address: d.address, lat: d.lat, lng: d.lng });
    try { await apiClient.post(`/rider/destinations/${d.id}/touch`, { rider_token: token }); } catch { /* */ }
  }

  async function saveCurrentDropoff() {
    if (!dropoff?.address || !newName.trim()) return;
    setSavingNew(true);
    try {
      const r = await apiClient.post("/rider/destinations", {
        rider_token: token,
        name: newName.trim(),
        address: dropoff.address,
        lat: dropoff.lat || null,
        lng: dropoff.lng || null,
      });
      const created = r?.data?.data;
      if (created) setSaved(prev => [created, ...prev.filter(x => x.id !== created.id)]);
      setNewName("");
    } catch { /* */ }
    finally { setSavingNew(false); }
  }

  async function removeSaved(id) {
    try {
      await apiClient.delete(`/rider/destinations/${id}?token=${encodeURIComponent(token)}`);
      setSaved(prev => prev.filter(x => x.id !== id));
    } catch { /* */ }
  }

  return (
    <main className="px-4 pb-24 space-y-4" data-testid="rider-compose">
      {saved.length > 0 && (
        <div className="rounded-2xl bg-white border border-[#E2E0D8] p-3 shadow-sm" data-testid="rider-saved-destinations">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 font-bold">Saved places · tap to fill</div>
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
            {saved.map(d => (
              <button
                key={d.id}
                onClick={() => pickSaved(d)}
                data-testid={`rider-saved-dest-${d.id}`}
                className="shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5"
                style={{ background: "#F1F8F4", border: "1px solid #C8DFD2", color: "#2B4C3B" }}
                title={d.address}
              >
                <span>📍</span>
                <span>{d.name}</span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); removeSaved(d.id); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); removeSaved(d.id); } }}
                  data-testid={`rider-saved-dest-remove-${d.id}`}
                  className="ml-1 text-slate-400 hover:text-red-600 cursor-pointer"
                  aria-label={`Remove saved place ${d.name}`}
                >×</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Payment card banner — deferred until a destination is entered, so the
          rider engages with "where to?" first, then sees payment context. */}
      {dropoff?.address && (
      <div className="rounded-2xl bg-white border border-[#E2E0D8] p-3 shadow-sm" data-testid="rider-payment-banner">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Payment</div>
          <button onClick={onAddCard} data-testid="rider-add-card-btn" className="text-[11px] text-forest font-bold underline">
            {paymentMethods.length ? "Add another card" : "Add card"}
          </button>
        </div>
        {defaultPm ? (
          <div className="flex items-center gap-2 mt-1">
            <div className="w-9 h-6 rounded bg-slate-900 text-white text-[10px] font-bold grid place-items-center uppercase">{defaultPm.brand}</div>
            <div className="text-sm font-semibold text-slate_navy">•••• {defaultPm.last4}</div>
            <div className="text-[11px] text-slate-500">exp {String(defaultPm.exp_month).padStart(2,"0")}/{String(defaultPm.exp_year).slice(-2)}</div>
            {paymentMethods.length > 1 && (
              <select
                value={defaultPmId || defaultPm.id}
                onChange={(e) => onSetDefault(e.target.value)}
                data-testid="rider-pm-select"
                className="ml-auto text-[11px] border border-[#E2E0D8] rounded-md px-2 py-1"
              >
                {paymentMethods.map((p) => (
                  <option key={p.id} value={p.id}>{p.brand.toUpperCase()} •••• {p.last4}</option>
                ))}
              </select>
            )}
          </div>
        ) : (
          <div className="text-xs text-slate-500 mt-1">
            No card on file yet. When you request a ride, we place a temporary authorization hold for the quoted fare. Nothing posts to your card until the trip completes.
          </div>
        )}
      </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E2E0D8] p-4 shadow-sm space-y-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Pickup</div>
          <GooglePlacesInput
            value={pickup?.address || ""}
            onChange={(v) => setPickup({ ...(pickup || {}), address: v })}
            onPlace={(p) => setPickup({ address: p.formatted_address, lat: p.lat, lng: p.lng })}
            testid="rider-pickup"
            placeholder="Detecting your location…"
            biasLat={pickup?.lat}
            biasLng={pickup?.lng}
          />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Where to?</div>
          <div className="relative">
            <div className="flex-1">
              <GooglePlacesInput
                value={dropoff.address || ""}
                onChange={(v) => setDropoff({ ...dropoff, address: v })}
                onPlace={(p) => setDropoff({ address: p.formatted_address, lat: p.lat, lng: p.lng })}
                testid="rider-dropoff"
                placeholder="Address, business name, or say it"
                biasLat={pickup?.lat}
                biasLng={pickup?.lng}
              />
            </div>
            <button
              onClick={startVoice}
              data-testid="rider-voice-btn"
              type="button"
              className={`absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full grid place-items-center transition z-20 ${voiceListening ? "bg-red-500 text-white animate-pulse" : "bg-[#F5F3EA] hover:bg-[#EDEBE1] text-slate_navy"}`}
              title="Hold to speak"
            >
              🎤
            </button>
          </div>
        </div>
        {dropoff?.address && !saved.some(d => d.address?.toLowerCase() === dropoff.address.toLowerCase()) && (
          <div className="flex items-center gap-2 pt-1" data-testid="rider-save-dest-row">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Save as… (e.g. Dialysis, Mom&apos;s house)"
              maxLength={40}
              data-testid="rider-save-dest-name"
              className="flex-1 px-3 py-1.5 rounded-full text-xs border border-[#E2E0D8]"
            />
            <button
              onClick={saveCurrentDropoff}
              disabled={savingNew || !newName.trim()}
              data-testid="rider-save-dest-btn"
              className="px-3 py-1.5 rounded-full text-xs font-bold text-white disabled:opacity-50"
              style={{ background: "#2B4C3B" }}
            >{savingNew ? "…" : "Save"}</button>
          </div>
        )}
      </div>

      {(() => {
        const currentVt = VEHICLE_TYPES.find(v => v.id === vehicleType) || VEHICLE_TYPES[0];
        const isDefault = vehicleType === "sedan" && !showVehiclePicker;
        // Progressive disclosure — only show the picker when the rider needs
        // a non-sedan vehicle (wheelchair / XL van). Sedan is the default so
        // 90%+ of riders never see this step. Bible §"Simplest possible experience".
        if (isDefault) {
          return (
            <div className="flex items-center justify-between px-1" data-testid="rider-vt-summary">
              <div className="text-[11px] text-slate-500">
                <span className="mr-1">{currentVt.emoji}</span>
                Riding <span className="font-semibold text-slate_navy">{currentVt.label}</span>
              </div>
              <button
                type="button"
                onClick={() => setShowVehiclePicker(true)}
                data-testid="rider-vt-change"
                className="text-[11px] text-forest font-bold underline"
              >
                Change ride type
              </button>
            </div>
          );
        }
        return (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 font-bold px-1">Ride type</div>
            <div className="grid grid-cols-3 gap-2">
              {VEHICLE_TYPES.map((v) => (
                <button
                  key={v.id}
                  onClick={() => { setVehicleType(v.id); if (v.id === "sedan") setShowVehiclePicker(false); }}
                  data-testid={`rider-vt-${v.id}`}
                  className={`rounded-xl border-2 py-3 px-2 text-left transition ${vehicleType === v.id ? "border-forest bg-white shadow-sm" : "border-[#E2E0D8] bg-white/60"}`}
                >
                  <div className="text-2xl">{v.emoji}</div>
                  <div className="text-xs font-bold mt-1">{v.label}</div>
                  <div className="text-[10px] text-slate-500">{v.sub}</div>
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      <button
        onClick={findRides}
        disabled={busy}
        data-testid="rider-find-btn"
        className="w-full py-4 rounded-2xl bg-forest hover:bg-forest-hover disabled:opacity-60 text-white text-lg font-bold shadow-lg"
      >
        {busy ? "Finding rides…" : "See rides"}
      </button>
    </main>
  );
}

/* ---------- Pick ---------- */
function PickStep({ quote, candidates, vehicleType, onBack, onHail, busy }) {
  const [route, setRoute] = useState(null);
  useEffect(() => {
    if (!quote?.pickup?.lat || !quote?.dropoff?.lat) return;
    apiClient.get("/rider/route", {
      params: {
        o_lat: quote.pickup.lat, o_lng: quote.pickup.lng,
        d_lat: quote.dropoff.lat, d_lng: quote.dropoff.lng,
      },
    }).then(({ data }) => {
      setRoute({ coords: polyline.decode(data.polyline), duration_min: data.duration_min, distance_mi: data.distance_mi });
    }).catch(() => setRoute(null));
  }, [quote?.pickup?.lat, quote?.pickup?.lng, quote?.dropoff?.lat, quote?.dropoff?.lng]);

  const center = quote?.pickup?.lat
    ? [(quote.pickup.lat + quote.dropoff.lat) / 2, (quote.pickup.lng + quote.dropoff.lng) / 2]
    : [42.3, -83.0];

  return (
    <main className="px-4 pb-24 space-y-3" data-testid="rider-pick">
      <button onClick={onBack} className="text-xs text-slate-500 flex items-center gap-1">
        ← Change destination
      </button>
      {/* Route preview map — shows the actual driving route */}
      <div className="rounded-2xl overflow-hidden border border-[#E2E0D8]" style={{ height: 220 }} data-testid="rider-route-preview">
        <MapContainer center={center} zoom={12} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap"/>
          {quote?.pickup?.lat && <Marker position={[quote.pickup.lat, quote.pickup.lng]} icon={riderIcon}><Popup>Pickup</Popup></Marker>}
          {quote?.dropoff?.lat && <Marker position={[quote.dropoff.lat, quote.dropoff.lng]} icon={riderIcon}><Popup>Drop-off</Popup></Marker>}
          {route?.coords && route.coords.length > 1 && (
            <Polyline positions={route.coords} pathOptions={{ color: "#2B4C3B", weight: 5, opacity: 0.85 }}/>
          )}
        </MapContainer>
      </div>
      <div className="rounded-xl bg-white border border-[#E2E0D8] p-3 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Ride total (hold at request)</div>
          <div className="text-2xl font-heading font-bold text-slate_navy" data-testid="rider-quote-total">
            <AnimatedNumber value={quote.fare.total} prefix="$" format={(v) => v.toFixed(2)} duration={520} />
          </div>
          <div className="text-[11px] text-slate-500">
            {route?.distance_mi ? `${route.distance_mi} mi` : `${quote.fare.distance_mi} mi`} · ~{Math.round(route?.duration_min || quote.fare.duration_min)} min · captured at drop-off
          </div>
        </div>
        <div className="text-xs text-slate-500 capitalize">{vehicleType.replace("_"," ")}</div>
      </div>

      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold px-1">Tap to request — {candidates.length} nearby</div>

      <ul className="space-y-2">
        {candidates.map((d) => (
          <li key={d.driver_id}>
            <button
              onClick={() => onHail(d)}
              disabled={busy}
              data-testid={`rider-hail-${d.driver_id}`}
              className="w-full text-left rounded-2xl bg-white border-2 border-[#E2E0D8] hover:border-forest disabled:opacity-60 p-4 flex items-center gap-3 shadow-sm transition"
            >
              <div className="w-12 h-12 rounded-full bg-forest text-white grid place-items-center text-lg font-bold">
                {(d.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate_navy">{d.name}</span>
                  <span className="text-xs text-amber-600 font-semibold">★ {d.rating.toFixed(2)}</span>
                </div>
                <div className="text-xs text-slate-500">{d.vehicle_desc} · {d.distance_mi} mi away{d.position?.is_live ? " · live" : ""}</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-heading font-bold text-slate_navy">{d.eta_min}</div>
                <div className="text-[10px] text-slate-500">min</div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </main>
  );
}

/* ---------- Waiting ---------- */
function WaitStep({ driver, trip, onCancel }) {
  const expiresAt = trip?.offer_expires_at ? new Date(trip.offer_expires_at) : null;
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => setRemaining(Math.max(0, Math.ceil((expiresAt - new Date()) / 1000)));
    tick(); const iv = setInterval(tick, 500);
    return () => clearInterval(iv);
  }, [trip?.offer_expires_at]);
  return (
    <main className="px-4 pb-24 space-y-4" data-testid="rider-wait">
      <div className="rounded-2xl bg-white border border-[#E2E0D8] p-6 text-center space-y-2">
        <div className="text-4xl animate-pulse">📡</div>
        <div className="text-lg font-heading font-bold text-slate_navy">Contacting {driver?.name}…</div>
        <div className="text-xs text-slate-500">Awaiting confirmation ({remaining}s)</div>
        <div className="pt-3">
          <button onClick={onCancel} data-testid="rider-cancel-btn" className="text-xs text-red-600 font-semibold underline">Cancel ride</button>
        </div>
      </div>
    </main>
  );
}

/* ---------- Accepted / live map ---------- */
function AcceptedStep({ token, driver, trip, pickup, onCancel }) {
  const [routeCoords, setRouteCoords] = useState(null);   // [[lat,lng], ...]
  const [routeEtaMin, setRouteEtaMin] = useState(null);   // minutes to next waypoint
  const center = useMemo(() => {
    if (driver?.position?.lat) return [driver.position.lat, driver.position.lng];
    if (pickup?.lat) return [pickup.lat, pickup.lng];
    return [42.3, -83.0];
  }, [driver, pickup]);

  // Decide the active leg based on trip status:
  //   EN_ROUTE / ACCEPTED: driver → pickup
  //   ON_TRIP: pickup → dropoff (driver moves along it)
  //   ARRIVED_*: no route needed
  const activeLeg = useMemo(() => {
    const dp = driver?.position;
    const dropoff = trip?.dropoff;
    const status = trip?.status;
    if (!dp?.lat) return null;
    if (["ACCEPTED", "EN_ROUTE"].includes(status) && pickup?.lat) {
      return { from: [dp.lat, dp.lng], to: [pickup.lat, pickup.lng] };
    }
    if (status === "ON_TRIP" && dropoff?.lat) {
      return { from: [dp.lat, dp.lng], to: [dropoff.lat, dropoff.lng] };
    }
    return null;
  }, [driver, trip, pickup]);

  // Fetch route polyline whenever the active leg changes materially (start/end
  // move by > ~150m from previous fetch to avoid API hammering)
  const lastLegRef = useRef(null);
  useEffect(() => {
    if (!activeLeg) { setRouteCoords(null); setRouteEtaMin(null); return; }
    const key = `${activeLeg.from[0].toFixed(3)},${activeLeg.from[1].toFixed(3)}→${activeLeg.to[0].toFixed(4)},${activeLeg.to[1].toFixed(4)}`;
    if (lastLegRef.current === key) return;
    lastLegRef.current = key;
    let cancelled = false;
    apiClient.get("/rider/route", {
      params: {
        o_lat: activeLeg.from[0], o_lng: activeLeg.from[1],
        d_lat: activeLeg.to[0],   d_lng: activeLeg.to[1],
      },
    }).then(({ data }) => {
      if (cancelled) return;
      const coords = polyline.decode(data.polyline);  // returns [[lat,lng], ...]
      setRouteCoords(coords);
      setRouteEtaMin(Math.round(data.duration_min || 0));
    }).catch(() => { /* fallback: straight line */ });
    return () => { cancelled = true; };
  }, [activeLeg]);

  const stage = (() => {
    switch (trip?.status) {
      case "ACCEPTED":   return { title: "Driver confirmed", sub: routeEtaMin ? `${routeEtaMin} min to your pickup` : "Heading your way", pill: "En route", tone: "amber" };
      case "EN_ROUTE":   return { title: "Driver is on the way", sub: routeEtaMin ? `${routeEtaMin} min to your pickup · meet at the curb` : "Meet at your pickup", pill: "En route", tone: "amber" };
      case "ARRIVED_PU": return { title: "Driver has arrived", sub: "Look for your driver at the curb", pill: "Arrived", tone: "violet" };
      case "ON_TRIP":    return { title: "On your way", sub: routeEtaMin ? `${routeEtaMin} min to destination` : "Enjoy the ride", pill: "In progress", tone: "forest" };
      case "ARRIVED_DO": return { title: "Arriving now", sub: "Almost at your destination", pill: "At destination", tone: "violet" };
      default:           return { title: `${driver?.name || "Driver"} confirmed`, sub: `Status: ${trip?.status}`, pill: "Confirmed", tone: "forest" };
    }
  })();
  const toneClass = { amber: "bg-amber-100 text-amber-800", violet: "bg-violet-100 text-violet-800", forest: "bg-forest text-white" }[stage.tone] || "bg-slate-100";
  const cancellable = ["ACCEPTED", "EN_ROUTE"].includes(trip?.status);

  return (
    <main className="px-4 pb-24 space-y-3" data-testid="rider-accepted">
      <div className="rounded-2xl bg-white border border-[#E2E0D8] p-4 flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-forest text-white grid place-items-center text-lg font-bold">
          {(driver?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-bold text-slate_navy" data-testid="rider-stage-title">{stage.title}</div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${toneClass}`} data-testid="rider-stage-pill">{stage.pill}</span>
          </div>
          <div className="text-xs text-slate-500">{driver?.vehicle_desc} · ★ {(driver?.rating || 5).toFixed(2)}</div>
          <div className="text-xs text-slate-500 mt-0.5" data-testid="rider-stage-sub">{stage.sub}</div>
        </div>
        {driver?.phone && (
          <a href={`tel:${driver.phone}`} data-testid="rider-call-driver" className="w-10 h-10 rounded-full bg-forest text-white grid place-items-center">📞</a>
        )}
      </div>
      <div className="rounded-2xl overflow-hidden border border-[#E2E0D8]" style={{ height: 380 }}>
        {/* 3D tilt hero — Google Maps vector tilt when a driver position exists.
            Falls back to Leaflet for pre-match / no-driver-position states. */}
        {driver?.position?.lat ? (
          <TiltMap3D
            pickup={pickup}
            dropoff={trip?.status === "ON_TRIP" ? trip.dropoff : undefined}
            driverPosition={driver.position}
            driverName={driver.name}
          />
        ) : (
          <MapContainer center={center} zoom={12} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap"/>
            {pickup?.lat && <Marker position={[pickup.lat, pickup.lng]} icon={riderIcon}><Popup>Your pickup</Popup></Marker>}
            {trip?.dropoff?.lat && trip.status === "ON_TRIP" && <Marker position={[trip.dropoff.lat, trip.dropoff.lng]} icon={riderIcon}><Popup>Your drop-off</Popup></Marker>}
            {routeCoords && routeCoords.length > 1 && (
              <Polyline positions={routeCoords} pathOptions={{ color: "#2B4C3B", weight: 5, opacity: 0.85 }}/>
            )}
          </MapContainer>
        )}
      </div>
      <div className="text-[11px] text-slate-500 text-center">
        Driver location and route update live during the ride.
      </div>
      {trip?.status === "ARRIVED_PU" && (
        <div className="rounded-2xl bg-white border-2 border-violet-300 p-4 flex items-center gap-4" data-testid="rider-wait-card">
          <WaitRing startedAt={trip?.arrived_pickup_at || trip?.status_changed_at || trip?.updated_at} size={96} />
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-violet-700">Driver is waiting</div>
            <div className="text-sm text-slate-700 mt-1">Please head to the curb — the wait timer is live.</div>
            <div className="text-[11px] text-slate-500 mt-2">3 min complimentary · then per-minute charges may apply.</div>
          </div>
        </div>
      )}
      {cancellable && (
        <button onClick={onCancel} data-testid="rider-cancel-btn" className="w-full py-2 rounded-md text-xs text-red-600 border border-red-200 hover:bg-red-50 font-semibold">
          Cancel ride
        </button>
      )}
      <SOSButton token={token} tripId={trip?.trip_id} />
    </main>
  );
}

/* ---------- Rider trip history (gap G14) ---------- */
function HistoryStep({ token, onBack, onRideAgain }) {
  const [trips, setTrips] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    apiClient.get(`/rider/trips?token=${encodeURIComponent(token)}`)
      .then(r => setTrips(r?.data?.items || []))
      .catch(() => setTrips([]))
      .finally(() => setLoading(false));
  }, [token]);

  const statusChip = (s) => {
    if (s === "COMPLETED") return { bg: "#DCFCE7", fg: "#166534", label: "Completed" };
    if (["CANCELED", "CANCELLED", "CANCELED_BY_RIDER", "CANCELLED_BY_RIDER"].includes(s)) return { bg: "#FEE2E2", fg: "#991B1B", label: "Canceled" };
    if (s === "NO_SHOW" || s === "NO_SHOW_CONFIRMED") return { bg: "#FEF3C7", fg: "#92400E", label: "No-show" };
    if (["ACCEPTED", "EN_ROUTE", "ARRIVED_PU", "ON_TRIP"].includes(s)) return { bg: "#DBEAFE", fg: "#1E40AF", label: "In progress" };
    return { bg: "#F1F5F9", fg: "#334155", label: s };
  };

  const fareOf = (t) => {
    if (t.captured_amount_cents) return (t.captured_amount_cents / 100).toFixed(2);
    if (t.fare_quote?.total) return Number(t.fare_quote.total).toFixed(2);
    return null;
  };

  return (
    <main className="px-4 pb-24 space-y-3" data-testid="rider-history">
      <button
        onClick={onBack}
        data-testid="rider-history-back"
        className="text-xs font-semibold text-forest hover:underline"
      >← Back</button>
      <div>
        <div className="text-lg font-bold text-slate_navy">Your rides</div>
        <div className="text-xs text-slate-500">Every ride you&apos;ve booked with MedexOVA. Tap <b>Ride again</b> on any completed trip to re-book it in one step.</div>
      </div>
      {loading ? (
        <div className="text-center py-8 text-sm text-slate-500">Loading…</div>
      ) : trips.length === 0 ? (
        <div className="text-center py-10 text-sm text-slate-500 rounded-2xl bg-white border border-[#E2E0D8]">
          No rides yet. Book one and it will appear here.
        </div>
      ) : (
        <ul className="space-y-2">
          {trips.map(t => {
            const chip = statusChip(t.status);
            const fare = fareOf(t);
            const canRideAgain = t.status === "COMPLETED" && !!(t.pickup?.address || t.dropoff?.address);
            return (
              <li key={t.trip_id} className="rounded-2xl bg-white border border-[#E2E0D8] p-3" data-testid={`rider-history-row-${t.trip_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-slate-500">{(t.created_at || "").slice(0, 10)}</div>
                    <div className="text-sm font-semibold text-slate_navy truncate mt-0.5">
                      <span className="text-green-600">●</span> {(t.pickup || {}).address || "—"}
                    </div>
                    <div className="text-sm text-slate_navy truncate">
                      <span className="text-red-600">●</span> {(t.dropoff || {}).address || "—"}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase"
                      style={{ background: chip.bg, color: chip.fg }}>{chip.label}</span>
                    {fare && <div className="text-sm font-bold text-slate_navy mt-1">${fare}</div>}
                    {t.refund && <div className="text-[10px] text-amber-700 mt-0.5">Refunded ${(t.refund.amount_usd || 0).toFixed(2)}</div>}
                  </div>
                </div>
                {canRideAgain && (
                  <button
                    onClick={() => onRideAgain?.(t)}
                    data-testid={`rider-ride-again-${t.trip_id}`}
                    className="mt-3 w-full text-sm font-bold py-2 rounded-lg bg-forest text-white transition-transform active:scale-95"
                  >
                    Ride again →
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}

/* ---------- SOS panic button ---------- */
function SOSButton({ token, tripId }) {
  const [open, setOpen] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [note, setNote] = React.useState("");

  async function trigger() {
    if (confirming) return;
    setConfirming(true);
    let lat = null, lng = null;
    try {
      if (navigator?.geolocation) {
        const pos = await new Promise((res) => navigator.geolocation.getCurrentPosition(
          (p) => res(p), () => res(null), { timeout: 3000 }
        ));
        if (pos?.coords) { lat = pos.coords.latitude; lng = pos.coords.longitude; }
      }
    } catch { /* geo denied — send without coords */ }
    try {
      await apiClient.post("/rider/sos", { rider_token: token, trip_id: tripId, lat, lng, note: note.trim() || null });
      setSent(true);
      if (navigator?.vibrate) { try { navigator.vibrate([120, 60, 120]); } catch { /* */ } }
    } catch (e) {
      toast.error("Couldn't reach MedexOVA. Please call 911 if this is an emergency.");
    } finally {
      setConfirming(false);
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="rider-sos-btn"
        className="w-full mt-2 py-3 rounded-full text-sm font-bold text-white flex items-center justify-center gap-2"
        style={{ background: "#B91C1C", boxShadow: "0 4px 20px rgba(185, 28, 28, 0.35)" }}
      >
        <span aria-hidden="true" style={{ fontSize: 18 }}>🆘</span>
        <span>I need help</span>
      </button>
      {open && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center"
          style={{ background: "rgba(15,23,42,0.75)" }}
          onClick={() => !confirming && !sent && setOpen(false)}
          data-testid="rider-sos-modal-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-2xl p-6 mx-4"
            style={{ background: "#fff", width: "min(420px, 92vw)", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}
            data-testid="rider-sos-modal"
          >
            {sent ? (
              <div className="space-y-3 text-center">
                <div style={{ fontSize: 40 }}>✅</div>
                <div className="text-lg font-bold text-slate_navy">Help is on the way</div>
                <div className="text-sm text-slate-600">
                  MedexOVA has been alerted and can see your location. If this is a life-threatening emergency, please also call <a href="tel:911" className="underline font-semibold">911</a>.
                </div>
                <button
                  onClick={() => { setOpen(false); setSent(false); setNote(""); }}
                  data-testid="rider-sos-close-btn"
                  className="mt-2 w-full py-2 rounded-full font-semibold text-white"
                  style={{ background: "#2B4C3B" }}
                >Close</button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="text-lg font-bold text-slate_navy">Need help right now?</div>
                  <div className="text-sm text-slate-600 mt-1">Tap the button below and MedexOVA will see your location, driver, and trip. You&apos;ll get a callback within 60 seconds.</div>
                </div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional: describe the situation…"
                  data-testid="rider-sos-note-input"
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg text-sm border border-slate-200"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => setOpen(false)}
                    disabled={confirming}
                    className="flex-1 py-2 rounded-full font-semibold border border-slate-300"
                    style={{ background: "#fff", color: "#334155" }}
                  >Cancel</button>
                  <button
                    onClick={trigger}
                    disabled={confirming}
                    data-testid="rider-sos-confirm-btn"
                    className="flex-1 py-2 rounded-full font-bold text-white"
                    style={{ background: "#B91C1C", opacity: confirming ? 0.6 : 1 }}
                  >{confirming ? "Sending…" : "Send SOS"}</button>
                </div>
                <div className="text-[11px] text-slate-500 text-center">
                  For life-threatening emergencies, always call <a href="tel:911" className="underline">911</a> first.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* ---------- Exhausted ---------- */
function ExhaustedStep({ onRetry }) {
  return (
    <main className="px-4 pb-24 space-y-4">
      <div className="rounded-2xl bg-white border border-[#E2E0D8] p-6 text-center space-y-2">
        <div className="text-4xl">🥲</div>
        <div className="text-lg font-heading font-bold text-slate_navy">No driver confirmed</div>
        <div className="text-xs text-slate-500">Every nearby driver either declined or didn&apos;t respond in time.</div>
        <button onClick={onRetry} data-testid="rider-retry-btn" className="mt-3 px-6 py-2 rounded-full bg-forest text-white text-sm font-bold">Try again</button>
      </div>
    </main>
  );
}

/* ---------- Completed + Tip + Rating ---------- */
function CompletedStep({ token, trip, driver, onDone }) {
  const [tipping, setTipping] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customVal, setCustomVal] = useState("");
  const [tipSent, setTipSent] = useState(null);
  const [rating, setRating] = useState(0);
  const [ratingSaved, setRatingSaved] = useState(false);
  const [hover, setHover] = useState(0);
  const fareTotal = Number(trip?.fare_quote?.total || 0);
  const fq = trip?.fare_quote || {};
  const baseAmt = Number(fq.base || 0);
  const distanceMi = Number(fq.distance_mi || 0);
  const durationMin = Number(fq.duration_min || 0);
  const perMile = Number(fq.per_mile || 0);
  const perMinute = Number(fq.per_minute || 0);
  const distanceLine = +(perMile * distanceMi).toFixed(2);
  const timeLine = +(perMinute * durationMin).toFixed(2);
  const serviceFee = Number(fq.service_fee || 0);
  const tax = Number(fq.tax || 0);
  const tipPaid = Number(trip?.tip_amount || tipSent || 0);
  const preset = [0.15, 0.20, 0.25];

  const sendTip = async (amount) => {
    if (tipping) return;
    const amt = Math.round(amount * 100) / 100;
    if (amt <= 0 || amt > 200) { toast.error("Tip must be between $0.01 and $200"); return; }
    setTipping(true);
    try {
      await apiClient.post(`/rider/rides/${trip.trip_id}/tip?token=${encodeURIComponent(token)}`, { amount_usd: amt });
      setTipSent(amt);
      toast.success(`Tip of $${amt.toFixed(2)} sent — thank you.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Tip failed");
    } finally { setTipping(false); }
  };

  const submitRating = async (stars) => {
    setRating(stars);
    try {
      await apiClient.post(`/rider/rides/${trip.trip_id}/rate-driver?token=${encodeURIComponent(token)}`, { stars });
      setRatingSaved(true);
      toast.success(`Thanks — you rated ${driver?.name?.split(" ")[0] || "your driver"} ${stars} star${stars>1?"s":""}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rating could not be saved");
    }
  };

  return (
    <main className="px-4 pb-24 space-y-3" data-testid="rider-completed">
      <div className="rounded-2xl bg-white border border-forest p-6 text-center space-y-2 shadow-sm">
        <div className="text-4xl">✅</div>
        <div className="font-heading text-2xl font-bold text-slate_navy">Ride complete</div>
        <div className="text-xs text-slate-500">${fareTotal.toFixed(2)} charged · a receipt is on the way to your email.</div>
        <Link
          to={`/ride/${token}/receipt/${trip?.trip_id}`}
          data-testid="rider-view-receipt-btn"
          className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold text-white"
          style={{ background: "linear-gradient(135deg,#0B1220,#2B4C3B)", boxShadow: "0 8px 20px -8px rgba(43,76,59,0.6)" }}
        >
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#10B981", boxShadow: "0 0 6px #10B981" }}/>
          View verified receipt
        </Link>
      </div>

      {/* On-screen fare breakdown — closes GW-9 gap G14. Rider never has to
          leave the app to answer "what did I pay for?". */}
      <div className="rounded-2xl bg-white border border-[#E2E0D8] p-4 shadow-sm space-y-2" data-testid="rider-fare-breakdown">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Fare breakdown</div>
        {baseAmt > 0 && (
          <div className="flex items-center justify-between text-sm" data-testid="rider-fare-base">
            <span className="text-slate-600">Base fare</span>
            <span className="font-semibold text-slate_navy">${baseAmt.toFixed(2)}</span>
          </div>
        )}
        {distanceMi > 0 && (
          <div className="flex items-center justify-between text-sm" data-testid="rider-fare-distance">
            <span className="text-slate-600">{distanceMi.toFixed(1)} miles</span>
            <span className="font-semibold text-slate_navy">${distanceLine.toFixed(2)}</span>
          </div>
        )}
        {durationMin > 0 && (
          <div className="flex items-center justify-between text-sm" data-testid="rider-fare-time">
            <span className="text-slate-600">{Math.round(durationMin)} min ride time</span>
            <span className="font-semibold text-slate_navy">${timeLine.toFixed(2)}</span>
          </div>
        )}
        {serviceFee > 0 && (
          <div className="flex items-center justify-between text-sm" data-testid="rider-fare-fee">
            <span className="text-slate-600">Service fee</span>
            <span className="font-semibold text-slate_navy">${serviceFee.toFixed(2)}</span>
          </div>
        )}
        {tax > 0 && (
          <div className="flex items-center justify-between text-sm" data-testid="rider-fare-tax">
            <span className="text-slate-600">Tax</span>
            <span className="font-semibold text-slate_navy">${tax.toFixed(2)}</span>
          </div>
        )}
        {tipPaid > 0 && (
          <div className="flex items-center justify-between text-sm" data-testid="rider-fare-tip">
            <span className="text-slate-600">Tip</span>
            <span className="font-semibold text-slate_navy">${tipPaid.toFixed(2)}</span>
          </div>
        )}
        <div className="border-t border-[#E2E0D8] pt-2 flex items-center justify-between">
          <span className="text-sm font-bold text-slate_navy">Total charged</span>
          <span className="text-lg font-heading font-bold text-forest" data-testid="rider-fare-total">
            ${(fareTotal + tipPaid).toFixed(2)}
          </span>
        </div>
      </div>

      {/* Rating */}
      <div className="rounded-2xl bg-white border border-[#E2E0D8] p-4 space-y-3 shadow-sm" data-testid="rider-rating-card">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Rate {driver?.name?.split(" ")[0] || "your driver"}</div>
          <div className="text-xs text-slate-500 mt-1">Your rating helps other riders choose great drivers.</div>
        </div>
        <div className="flex items-center justify-center gap-2" onMouseLeave={() => setHover(0)}>
          {[1,2,3,4,5].map((s) => (
            <button
              key={s}
              onClick={() => !ratingSaved && submitRating(s)}
              onMouseEnter={() => setHover(s)}
              disabled={ratingSaved}
              data-testid={`rider-star-${s}`}
              className={`text-4xl transition-transform ${((hover || rating) >= s) ? "text-amber-400 scale-110" : "text-slate-300"} ${ratingSaved ? "cursor-default" : "cursor-pointer hover:scale-125"}`}
              aria-label={`${s} stars`}
            >
              ★
            </button>
          ))}
        </div>
        {ratingSaved && <div className="text-[11px] text-forest text-center font-semibold">Rating submitted · thank you</div>}
      </div>

      {tipSent == null ? (
        <div className="rounded-2xl bg-white border border-[#E2E0D8] p-4 space-y-3 shadow-sm" data-testid="rider-tip-card">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Add a tip for {driver?.name?.split(" ")[0] || "your driver"}?</div>
            <div className="text-xs text-slate-500 mt-1">100% of your tip goes to the driver.</div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {preset.map((p) => {
              const amt = Math.max(1, Math.round(fareTotal * p * 100) / 100);
              return (
                <button
                  key={p}
                  onClick={() => sendTip(amt)}
                  disabled={tipping}
                  data-testid={`rider-tip-${Math.round(p*100)}`}
                  className="py-3 rounded-xl border-2 border-[#E2E0D8] hover:border-forest bg-white text-center transition disabled:opacity-60"
                >
                  <div className="text-lg font-heading font-bold text-slate_navy">{Math.round(p * 100)}%</div>
                  <div className="text-[11px] text-slate-500">${amt.toFixed(2)}</div>
                </button>
              );
            })}
          </div>
          {!customOpen ? (
            <button onClick={() => setCustomOpen(true)} data-testid="rider-tip-custom-btn" className="w-full py-2 rounded-md text-sm text-forest font-semibold border border-[#E2E0D8]">
              Custom amount
            </button>
          ) : (
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <input
                  type="number" step="0.01" min="0.01" max="200"
                  value={customVal}
                  onChange={(e) => setCustomVal(e.target.value)}
                  data-testid="rider-tip-custom-input"
                  className="w-full border border-[#E2E0D8] rounded-md py-2 pl-7 pr-3 text-base"
                  placeholder="0.00"
                  autoFocus
                />
              </div>
              <button
                onClick={() => sendTip(Number(customVal))}
                disabled={tipping || !customVal}
                data-testid="rider-tip-custom-submit"
                className="px-4 py-2 rounded-md bg-forest text-white font-bold text-sm disabled:opacity-60"
              >
                Send
              </button>
            </div>
          )}
          <button onClick={onDone} data-testid="rider-tip-skip" className="w-full py-2 text-xs text-slate-500 underline">
            Skip tip · Book another ride
          </button>
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-forest p-4 text-center space-y-2" data-testid="rider-tip-sent">
          <div className="text-3xl">💚</div>
          <div className="font-heading text-lg font-bold text-slate_navy">Tip of ${tipSent.toFixed(2)} sent</div>
          <div className="text-xs text-slate-500">Thanks — {driver?.name?.split(" ")[0] || "your driver"} will appreciate that.</div>
          <button onClick={onDone} data-testid="rider-book-another" className="mt-3 px-6 py-2 rounded-full bg-forest text-white text-sm font-bold">
            Book another ride
          </button>
        </div>
      )}
    </main>
  );
}


// ────────────────────────────────────────────────────────────────
// RiderStatusRow · gamification strip on the compose page
// See /app/memory/DESIGN_LANGUAGE_v2.md §8.1
// ────────────────────────────────────────────────────────────────
function riderTier(rides) {
  if (rides >= 120) return { label: "Champion", next: null, toNext: 0 };
  if (rides >= 40) return { label: "Ace", next: "Champion", toNext: 120 - rides };
  if (rides >= 10) return { label: "Regular", next: "Ace", toNext: 40 - rides };
  return { label: "Verified Rider", next: "Regular", toNext: Math.max(1, 10 - rides) };
}

function RiderStatusRow({ rider }) {
  const rides = rider?.lifetime_rides || 0;
  const t = riderTier(rides);
  const goalToday = rides < 4 ? 1 : rides < 20 ? 1 : 2;
  const goalLabel = rides === 0
    ? "Your first ride unlocks Verified Rider status."
    : `One ride today keeps your streak. Currently ${rides} verified rides.`;
  const tickerItems = [
    "A rider in your area was verified 2 minutes ago",
    "Chain-of-custody stitched on a completed trip · 4 min ago",
    "New driver joined the network this hour",
    "Trip receipt hash locked · 6 min ago",
    "Beacon lit up in the next zip · 8 min ago",
  ];
  return (
    <div className="px-4 pb-3" data-testid="rider-status-row">
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
        {/* Tier + streak strip */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
          padding: "10px 14px", borderRadius: 12,
          background: "linear-gradient(135deg,#0B1220 0%,#142433 100%)",
          border: "1px solid rgba(59,130,246,0.28)",
          boxShadow: "0 8px 20px -8px rgba(0,0,0,0.35)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <TierBadgeInline label={t.label} accent="#3B82F6" />
            <div style={{ fontSize: 11, color: "#94A3B8" }}>
              {rides} verified {rides === 1 ? "ride" : "rides"}
              {t.next && <> · <span style={{ color: "#3B82F6", fontWeight: 700 }}>{t.toNext} to {t.next}</span></>}
            </div>
          </div>
          <StreakFlameInline count={Math.floor(rides / 4) + 1} unit="wk" color="#3B82F6" />
        </div>
        {/* Daily goal card */}
        <div style={{
          padding: 12, borderRadius: 12,
          background: "#fff", border: "1px solid #E2E0D8",
        }} data-testid="rider-daily-goal">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#64748B", fontWeight: 800 }}>Today&apos;s Goal</div>
            <div style={{ fontSize: 12, color: "#0B1220", fontWeight: 800 }}>0/{goalToday}</div>
          </div>
          <div style={{ fontSize: 12, color: "#334155", marginBottom: 8 }}>{goalLabel}</div>
          <div style={{ height: 3, background: "#E2E0D8", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: "0%", background: "#3B82F6", transition: "width 500ms" }}/>
          </div>
        </div>
        {/* Anonymized ticker */}
        <div style={{
          padding: "8px 12px", borderRadius: 10,
          background: "#F5F3EA", border: "1px solid #E2E0D8",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#10B981", boxShadow: "0 0 6px #10B981" }}/>
          <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 800 }}>Live</span>
          <span style={{ fontSize: 11, color: "#334155", flex: 1, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
            {tickerItems[0]}
          </span>
        </div>
      </div>
    </div>
  );
}

function TierBadgeInline({ label, accent }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 10px", borderRadius: 999,
      background: `${accent}1F`, border: `1px solid ${accent}55`,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: accent, boxShadow: `0 0 6px ${accent}` }}/>
      <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.16em", color: accent, fontWeight: 800 }}>{label}</span>
    </div>
  );
}

function StreakFlameInline({ count, unit, color }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill={color}>
        <path d="M12 2s3 4 3 8a3 3 0 01-6 0c0-1 .5-2 1-3-1 1-2 2-3 4a5 5 0 1010 0c0-3-2-6-5-9z"/>
      </svg>
      <span style={{ fontSize: 10, fontWeight: 800, color, fontFamily: "ui-monospace,Menlo,monospace" }}>{count} {unit}</span>
    </span>
  );
}
